import { ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, alpha, Stack, Link, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { motion } from 'framer-motion';
import { DownOutlined } from '@ant-design/icons';
import { PageUrl } from 'const/page-url';
import { SECTION_PY } from 'const/section';

// ==============================|| FAQ SECTION ||============================== //
// 検討の最終段階で残る不安(乗り換えコスト・責任範囲・個人情報)を先に潰すためのセクション。
// とくに「AIの出力はあくまで下書きである」「加算の算定を保証しない」の2点は、
// 過度な期待による導入後のミスマッチを防ぐために必ず残すこと。

type Faq = {
    q: string;
    a: ReactNode;
    /**
     * 構造化データ(FAQPage)に出す回答のテキスト版。
     * a がリンクなどの要素を含み、そのまま文字列として扱えないときだけ指定する。
     */
    plainA?: string;
};

const FaqSection = () => {
    const primaryColor = '#2aa1af';

    const faqs: Faq[] = [
        {
            q: 'いま使っている請求ソフトを切り替える必要はありますか？',
            a: 'いいえ、切り替えは不要です。OHANA AI は請求機能を持たないため、請求・勤怠・給与などは現在お使いのシステムをそのままご利用いただけます。OHANA AI はその隣で、個別支援計画書や加算書類など、書類作成の部分だけを担当します。'
        },
        {
            q: 'AIが作成した書類は、そのまま提出できますか？',
            a: 'AIが作成するのはあくまで下書きです。お子さまの状況を最もよく把握されているのは現場の職員の皆さまですので、内容の確認と加筆・修正を前提としています。最終的な内容の判断と責任は、児童発達支援管理責任者の皆さまに担っていただく設計です。'
        },
        {
            q: '加算の算定や、減算の回避は保証されますか？',
            a: '保証はいたしかねます。OHANA AI は算定要件の考え方や書類の整備をサポートするものであり、算定可否の最終的な判断は指定権者(自治体)が行います。要件の解釈は自治体によって異なる場合もあるため、実際の算定にあたっては必ず指定権者へご確認ください。'
        },
        {
            q: 'お子さまの情報や個人情報は、どのように扱われますか？',
            a: (
                <>
                    個人情報保護法に基づき適切に管理しています。外部のAI事業者へ情報を送信する場合の取り扱いについても、内容を公表しています。詳しくは
                    <Link component={RouterLink} to={PageUrl.PrivacyPolicy.urlWithSlash} sx={{ color: primaryColor, fontWeight: 700, mx: 0.5 }}>
                        プライバシーポリシー
                    </Link>
                    および
                    <Link component={RouterLink} to={PageUrl.AiDataTransfer.urlWithSlash} sx={{ color: primaryColor, fontWeight: 700, mx: 0.5 }}>
                        外部AI事業者への個人情報の送信について
                    </Link>
                    をご覧ください。
                </>
            ),
            plainA: '個人情報保護法に基づき適切に管理しています。外部のAI事業者へ情報を送信する場合の取り扱いについても、内容を公表しています。詳しくはプライバシーポリシー、および外部AI事業者への個人情報の送信についてをご覧ください。'
        },
        {
            q: '無料で試すことはできますか？',
            a: 'はい。1ヶ月間の無料トライアルをご用意しています。初期費用もかかりませんので、実際の業務のなかで操作感や書類の仕上がりをお確かめいただいたうえで、ご契約をご判断いただけます。お申し込みは資料請求フォームから承ります。'
        },
        {
            q: '導入にはどれくらいの準備が必要ですか？',
            a: 'インストール作業は不要で、ブラウザからすぐにご利用いただけます。初期費用もかかりません。導入時の初期設定や操作のご説明については、個別にサポートいたします。詳しくは資料請求フォームよりお申し付けください。'
        },
        {
            q: '1教室からでも契約できますか？',
            a: 'はい、1教室からご契約いただけます。料金は契約教室数に応じた従量制のため、多店舗で運営されている法人さまも、事業所単位で必要な分だけご利用いただけます。'
        }
    ];

    // FAQPage の構造化データ。検索結果にQ&Aが展開され、クリック前に不安を解消できる。
    // 画面に見えている質問・回答とここの内容が食い違うとGoogleのガイドライン違反になるため、
    // faqs を書き換えたらこちらは自動で追従する形にしてある(手で二重管理しないこと)。
    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.plainA ?? String(faq.a)
            }
        }))
    };

    return (
        <Box component="section" id="faq" sx={{ py: SECTION_PY, bgcolor: '#f4f7f7', overflow: 'hidden' }}>
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
            </Helmet>

            <Container maxWidth="md">
                <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.35 }}
                    >
                        <Typography
                            variant="caption"
                            component="span"
                            sx={{ color: primaryColor, fontWeight: 900, letterSpacing: '0.2em', display: 'block', mb: 2 }}
                        >
                            FAQ
                        </Typography>
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{ fontWeight: 900, fontSize: { xs: '1.85rem', md: '2.5rem' }, color: '#1a1a1a' }}
                        >
                            よくあるご質問
                        </Typography>
                    </motion.div>
                </Box>

                <Stack spacing={2}>
                    {faqs.map((faq, index) => (
                        <motion.div
                            key={faq.q}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.3 + index * 0.07 }}
                        >
                            <Accordion
                                disableGutters
                                elevation={0}
                                sx={{
                                    borderRadius: '20px !important',
                                    border: '1px solid #e5eaea',
                                    bgcolor: '#fff',
                                    overflow: 'hidden',
                                    '&:before': { display: 'none' }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<DownOutlined style={{ color: primaryColor, fontSize: '0.9rem' }} />}
                                    sx={{
                                        px: { xs: 2.5, md: 3.5 },
                                        py: 1.5,
                                        // テーマ既定(themes/overrides/AccordionSummary)がグレー背景かつ
                                        // アイコン左寄せ・90度回転になっているため、ここで打ち消す
                                        bgcolor: '#fff',
                                        flexDirection: 'row',
                                        '& .MuiAccordionSummary-expandIconWrapper': {
                                            transform: 'rotate(0deg)',
                                            '&.Mui-expanded': { transform: 'rotate(180deg)' }
                                        },
                                        '& .MuiAccordionSummary-content': { ml: 0 }
                                    }}
                                >
                                    <Typography
                                        variant="h3"
                                        component="h3"
                                        sx={{ fontWeight: 800, fontSize: { xs: '0.98rem', md: '1.08rem' }, color: '#1a1a1a', lineHeight: 1.6, pr: 2 }}
                                    >
                                        {faq.q}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: { xs: 2.5, md: 3.5 }, pb: 3, pt: 0 }}>
                                    <Box sx={{ height: '1px', bgcolor: alpha(primaryColor, 0.12), mb: 2.5 }} aria-hidden="true" />
                                    <Typography component="p" sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.9 }}>
                                        {faq.a}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        </motion.div>
                    ))}
                </Stack>
            </Container>
        </Box>
    );
};

export default FaqSection;
